import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 모듈 복구 — 래치 해제 `POST /modules/{id}/recovery/reset`(`IFC-RO-31`) · 복구 개시 `POST /modules/{id}/recovery/start`(`IFC-RO-42`).
 *
 * 실물(`openapi.json` · `ctcells-core/app/views/modules.py` · 2026-09-29)
 *
 * | 칸 | 래치 해제 | 복구 개시 |
 * | --- | --- | --- |
 * | 본문 | `ResetModuleRecoveryLatchBody{command_id, reason}` — **둘 다 필수** | `StartModuleRecoveryBody{command_id, reason, force?}` — `reason` 필수 |
 * | 헤더 | `X-Control-Token` 필수 | `X-Control-Token` 필수 |
 * | 성공 | 200 `ModuleSnapshot` | 202(빈 스키마) — 결과(`RECOVERY → IDLE | ERROR`)는 모듈 상태가 낸다 |
 * | 거절 | 모듈 409 를 그대로 옮긴다 | 모듈 409 그대로 — `MODULE_HANDOFF_DOOR_OPEN` · `MODULE_SAFETY_HOLD_ACTIVE` · `MODULE_BUSY` · 래치(리셋 선행) |
 *
 * 🔴 **`reason` 을 늘 싣는다.** 래치 해제 훅이 종전에 `command_id` 만 보내 실서버가 **422** 였다(2026-09-28 히나 실측 ·
 *    `ResetModuleRecoveryLatchBody.reason` required). 서버는 받은 사유를 모듈 본문과 감사에 그대로 싣는다.
 * 🔴 **자동 복구가 아니다**(`SM-MOD-10` · DP-02) — 한 번 누르면 한 사이클만 돈다. 되풀이하지 않는다.
 * ⚠ `force` 는 명령 개방 구간(도어 열림) 문 하나만 건너뛴다 — «사람이 도어 쪽 안전을 확인했다»는 뜻이다.
 *   기본값은 보내지 않는 것(서버 기본 `false`)이다.
 */

export interface ResetModuleRecoveryLatchVars {
  moduleId: string
  /** 🔴 필수 — 무엇을 고쳤는지 감사에 남는다 */
  reason: string
  commandId?: string
}

export function resetModuleRecoveryLatch({
  moduleId,
  reason,
  commandId,
}: ResetModuleRecoveryLatchVars): Promise<unknown> {
  return orchestrator.request<unknown>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/recovery/reset`,
    body: { command_id: commandId ?? newCommandId(), reason },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

/** 🔴 `X-Control-Token` 대상. 복구 래치 해제 — 자동이 아니라 명시적 행위다(DP-02) */
export const useResetModuleRecoveryLatch = createCommand<ResetModuleRecoveryLatchVars, unknown>({
  send: resetModuleRecoveryLatch,
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})

export interface StartModuleRecoveryVars {
  moduleId: string
  /** 🔴 필수 — 복구를 여는 사유 */
  reason: string
  /** 도어 개방 문 하나만 건너뛴다 — 사람이 도어 쪽 안전을 확인했을 때만 */
  force?: boolean
  commandId?: string
}

export function startModuleRecovery({
  moduleId,
  reason,
  force,
  commandId,
}: StartModuleRecoveryVars): Promise<unknown> {
  return orchestrator.request<unknown>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/recovery/start`,
    body: {
      command_id: commandId ?? newCommandId(),
      reason,
      ...(force === undefined ? {} : { force }),
    },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

/** 🔴 `X-Control-Token` 대상. `ERROR` 모듈의 복구 한 사이클 */
export const useStartModuleRecovery = createCommand<StartModuleRecoveryVars, unknown>({
  send: startModuleRecovery,
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})

/**
 * 파지 기억 초기화 `POST /modules/{id}/grips/forget`(`IFC-RO-53` · 실서버 `ForgetModuleGripsBody` 2026-09-29).
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `{command_id, reason, robot_id?}` — `reason` 필수(감사) · `robot_id` 없으면 그 모듈의 그리퍼 전부 |
 * | 헤더 | `X-Control-Token` 필수(`views/modules.py` `forget_module_grips` · `require_control_lock`) |
 * | 성공 | 200 `ModuleSnapshot` |
 * | 거절 | 모듈 409 그대로 — 도는 일이 있다(`MODULE_BUSY`) · 파지를 실물 판독으로 내는 운영 드라이버 |
 *
 * 🔴 **가동부를 움직이지 않는다 — 장부의 «쥐고 있다»만 지운다.** 지우면 L2 의 쥔 로봇 가드가 풀려 초기화가 나간다.
 *    사람이 파지물을 비웠음을 확인했다는 뜻이라, **내려놓을 길이 있는 파지(HANDOFF 6축 → `place_held`)에 쓰지 마라** —
 *    트레이가 장부에서 사라진다.
 */
export interface ForgetModuleGripsVars {
  moduleId: string
  /** 🔴 필수 — 누가 무엇을 확인하고 비웠는지 */
  reason: string
  /** 대상 그리퍼(`grips[].robot_id`). 없으면 전부 */
  robotId?: string
  commandId?: string
}

export function forgetModuleGrips({
  moduleId,
  reason,
  robotId,
  commandId,
}: ForgetModuleGripsVars): Promise<unknown> {
  return orchestrator.request<unknown>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/grips/forget`,
    body: {
      command_id: commandId ?? newCommandId(),
      reason,
      ...(robotId === undefined ? {} : { robot_id: robotId }),
    },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

/** 🔴 `X-Control-Token` 대상. 파지 기억 초기화 — 사람이 비웠음을 확인한 명시적 행위다 */
export const useForgetModuleGrips = createCommand<ForgetModuleGripsVars, unknown>({
  send: forgetModuleGrips,
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})

/**
 * 자리 비움 확약 `POST /modules/{id}/control/handoff-release`(`IFC-RO-54` · v0.9.4) — 사람이 눈으로 확인하고 핸드오프 자리 하나를 «비었다»로 적는다.
 * 모듈 장부에 그 자리 행이 생겨 점유가 「모름」에서 「빔」이 된다. 🔴 `X-Control-Token` 대상 · 사유는 감사에 그대로 실린다.
 */
export interface ReleaseHandoffPointVars {
  moduleId: string
  pointId: string
  reason: string
  commandId?: string
}

export const useReleaseHandoffPoint = createCommand<ReleaseHandoffPointVars, unknown>({
  send: ({ moduleId, pointId, reason, commandId }) =>
    orchestrator.request<unknown>({
      method: 'POST',
      path: `/modules/${encodeURIComponent(moduleId)}/control/handoff-release`,
      body: { command_id: commandId ?? newCommandId(), point_id: pointId, reason },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ moduleId }) => [keys.modules.detail(moduleId), keys.modules.all()],
})
